import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import BrandLogo from "../components/common/BrandLogo";
import { Helmet } from "react-helmet-async";
import { motion, useScroll, useTransform } from "framer-motion";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Phone, MessageCircle, Plane, Waves, TrainFront, LandPlot, Sparkles, ChevronDown, MapPin, TrendingUp, Shield, Compass, Award, Building2, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { useSiteSettings } from "../lib/settings";
import { resolveImage } from "../components/admin/ImageUpload";
import BitrixFormEmbed from "../components/common/BitrixFormEmbed";
import Footer from "../components/layout/Footer";
import api from "../lib/api";
import "./yenisehir.css";

gsap.registerPlugin(ScrollTrigger);

const PEXELS_FALLBACK_VIDEO = "https://cdn.coverr.co/videos/coverr-aerial-view-of-a-city-8290/1080p.mp4";
const HERO_STILL = "https://images.unsplash.com/photo-1524231757912-21f4fe3a7200?auto=format&fit=crop&w=2400&q=80";

/* ------------------------------------------------------------------ */
/* Custom cursor                                                        */
/* ------------------------------------------------------------------ */
const PremiumCursor = () => {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current; if (!el) return;
    let raf; const pos = { x: window.innerWidth / 2, y: window.innerHeight / 2 };
    const target = { ...pos };
    const move = (e) => { target.x = e.clientX; target.y = e.clientY; };
    const down = () => el.classList.add("is-clicking");
    const up = () => el.classList.remove("is-clicking");
    const over = (e) => {
      const t = e.target;
      if (t.closest("a, button, [data-hover], input, textarea, select")) el.classList.add("is-hover");
      else el.classList.remove("is-hover");
    };
    const tick = () => {
      pos.x += (target.x - pos.x) * 0.22;
      pos.y += (target.y - pos.y) * 0.22;
      el.style.transform = `translate(${pos.x}px, ${pos.y}px) translate(-50%, -50%)`;
      raf = requestAnimationFrame(tick);
    };
    window.addEventListener("mousemove", move);
    window.addEventListener("mousedown", down);
    window.addEventListener("mouseup", up);
    document.addEventListener("mouseover", over);
    tick();
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mousedown", down);
      window.removeEventListener("mouseup", up);
      document.removeEventListener("mouseover", over);
    };
  }, []);
  return <div ref={ref} className="yn-cursor" aria-hidden="true" />;
};

/* ------------------------------------------------------------------ */
/* Loading screen (logo stroke draw)                                   */
/* ------------------------------------------------------------------ */
const LoadingScreen = ({ done }) => (
  <div className={`yn-loading ${done ? "is-done" : ""}`} aria-hidden={done}>
    <svg viewBox="0 0 400 120" role="img" aria-label="Fırat Construction">
      <text x="200" y="82" textAnchor="middle" className="stroke-anim">FIRAT</text>
    </svg>
  </div>
);

/* ------------------------------------------------------------------ */
/* Scroll progress rail                                                */
/* ------------------------------------------------------------------ */
const ScrollProgress = () => {
  const { scrollYProgress } = useScroll();
  const y = useTransform(scrollYProgress, (v) => `scaleY(${v})`);
  return (
    <div className="yn-progress" aria-hidden="true">
      <motion.div className="yn-progress-inner" style={{ transform: y }} />
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* Reveal on scroll (IntersectionObserver — cheap for many nodes)      */
/* ------------------------------------------------------------------ */
const useReveal = () => {
  useEffect(() => {
    const els = document.querySelectorAll(".yn-reveal, .yn-mask-reveal");
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) { e.target.classList.add("is-visible"); io.unobserve(e.target); }
      });
    }, { threshold: 0.15, rootMargin: "0px 0px -80px 0px" });
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);
};

/* ------------------------------------------------------------------ */
/* Mobile detection (Google Ads landing is mobile-first)               */
/* ------------------------------------------------------------------ */
const useIsMobile = (bp = 768) => {
  const [m, setM] = useState(typeof window !== "undefined" ? window.innerWidth < bp : false);
  useEffect(() => {
    const on = () => setM(window.innerWidth < bp);
    on();
    window.addEventListener("resize", on);
    return () => window.removeEventListener("resize", on);
  }, [bp]);
  return m;
};

/* ------------------------------------------------------------------ */
/* Animated counter                                                     */
/* ------------------------------------------------------------------ */
const Counter = ({ to = 100, suffix = "", duration = 1800 }) => {
  const ref = useRef(null);
  const [val, setVal] = useState(0);
  useEffect(() => {
    const el = ref.current; if (!el) return;
    let raf; let started = false;
    const start = (t0) => {
      const step = (t) => {
        const p = Math.min(1, (t - t0) / duration);
        const eased = 1 - Math.pow(1 - p, 3);
        setVal(Math.round(to * eased));
        if (p < 1) raf = requestAnimationFrame(step);
      };
      raf = requestAnimationFrame(step);
    };
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting && !started) { started = true; start(performance.now()); }
      });
    }, { threshold: 0.4 });
    io.observe(el);
    return () => { cancelAnimationFrame(raf); io.disconnect(); };
  }, [to, duration]);
  return <span ref={ref} className="tabular-nums">{val}{suffix}</span>;
};

/* ------------------------------------------------------------------ */
/* Mapbox 3D interactive map                                           */
/* ------------------------------------------------------------------ */
/* Kategori renk skalası — e-iPAT `kategori` alanı gerçek değerlerine göre */
const CATEGORY_COLORS = {
  "yerlesim":       "#C9A227",  // gold — konut/yerleşim
  "ticari":         "#E85A4F",  // ruby
  "yesil":          "#4FB380",  // green
  "sosyal_egitim":  "#7A5FFF",  // violet
  "sosyal_saglik":  "#4FB3E8",  // sky
  "sosyal_ibadet":  "#B08A3E",  // brass
  "sosyal_kultur":  "#D97BB4",  // pink
  "sosyal_spor":    "#F0B429",  // amber
  "kamu":           "#8B93A8",  // steel
  "ulasim":         "#6B7280",  // grey
};
const CATEGORY_LABELS = {
  "yerlesim":       "Yerleşim",
  "ticari":         "Ticari",
  "yesil":          "Yeşil Alan",
  "sosyal_egitim":  "Eğitim",
  "sosyal_saglik":  "Sağlık",
  "sosyal_ibadet":  "İbadet",
  "sosyal_kultur":  "Kültür",
  "sosyal_spor":    "Spor",
  "kamu":           "Kamu",
  "ulasim":         "Ulaşım",
};
const categoryColor = (k) => CATEGORY_COLORS[k] || "#8B8D93";
const categoryLabel = (k) => CATEGORY_LABELS[k] || (k || "Diğer");

/**
 * Escape untrusted string content before injecting into HTML strings that
 * Leaflet passes to bindPopup/bindTooltip/divIcon (which render as raw HTML).
 * Protects against unbalanced quotes, `<`, `>`, and XSS if an admin or the
 * external imar-plani API is ever compromised.
 */
const escapeHtml = (v) => {
  if (v === null || v === undefined) return "";
  return String(v)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
};

const InteractiveMap = ({ imarUrl, eipatBase, customMarkers = [], projectMarkers = [], onParcelInquiry }) => {
  const ref = useRef(null);
  const mapRef = useRef(null);
  const geoLayerRef = useRef(null);
  const projectsLayerRef = useRef(null);
  const [selected, setSelected] = useState(null);
  const [loadingParcels, setLoadingParcels] = useState(false);
  const [parcelCount, setParcelCount] = useState(0);
  const [category, setCategory] = useState("Tümü");
  const [categoryList, setCategoryList] = useState([]);

  // Init Leaflet map
  useEffect(() => {
    if (!ref.current || mapRef.current) return;
    const map = L.map(ref.current, {
      center: [41.20, 28.68],
      zoom: 12,
      zoomControl: true,
      attributionControl: true,
      preferCanvas: true,   // faster for 4k polygons
    });
    mapRef.current = map;

    // Light modern tile layer (CartoDB Voyager) — clean streets + subtle labels
    L.tileLayer("https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png", {
      attribution: '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> katkıda bulunanlar · © <a href="https://carto.com/attributions">CARTO</a>',
      subdomains: "abcd",
      maxZoom: 20,
    }).addTo(map);

    // Track zoom level on the map root so CSS can scale labels responsively
    const applyZoomClass = () => {
      const z = map.getZoom();
      const root = map.getContainer();
      root.classList.remove("yn-map-zoom-low", "yn-map-zoom-mid", "yn-map-zoom-high");
      if (z < 11) root.classList.add("yn-map-zoom-low");
      else if (z < 13) root.classList.add("yn-map-zoom-mid");
      else root.classList.add("yn-map-zoom-high");
    };
    applyZoomClass();
    map.on("zoomend", applyZoomClass);

    // POI markers — kullanıcı tarafından belirlenen konumlar
    const pois = [
      { latlng: [41.2611, 28.7419], label: "İstanbul Havalimanı", icon: "✈" },
      { latlng: [41.143698, 28.672322], label: "Kanal İstanbul (planlanan)", icon: "≋" },
      { latlng: [41.238210, 28.618791], label: "Kuzey Marmara Otoyolu", icon: "═" },
    ];
    // Base POIs (hard-coded havalimanı / kanal / otoyol) — render once
    pois.forEach((p) => {
      const html = `<div class="yn-poi-marker"><span class="dot"></span><span class="label">${escapeHtml(p.icon)} ${escapeHtml(p.label)}</span></div>`;
      const marker = L.marker(p.latlng, { icon: L.divIcon({ html, className: "yn-poi-icon", iconSize: [14, 14], iconAnchor: [7, 7] }) }).addTo(map);
      if (p.link) {
        marker.on("click", () => { window.open(p.link, "_blank", "noopener"); });
        marker.getElement().style.pointerEvents = "auto";
        marker.getElement().style.cursor = "pointer";
      }
    });

    return () => { map.remove(); mapRef.current = null; };
  }, []);

  // Custom admin-defined markers (from Site Settings) — re-render whenever admin edits them
  const customLayerRef = useRef(null);
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (customLayerRef.current) { customLayerRef.current.remove(); customLayerRef.current = null; }
    if (!Array.isArray(customMarkers) || customMarkers.length === 0) return;
    const group = L.layerGroup().addTo(map);
    customLayerRef.current = group;
    customMarkers.forEach((m) => {
      const lat = parseFloat(m.lat); const lng = parseFloat(m.lng);
      if (!Number.isFinite(lat) || !Number.isFinite(lng) || !m.label) return;
      const html = `<div class="yn-poi-marker"><span class="dot"></span><span class="label">${escapeHtml(m.icon || "◉")} ${escapeHtml(m.label)}</span></div>`;
      const marker = L.marker([lat, lng], { icon: L.divIcon({ html, className: "yn-poi-icon", iconSize: [14, 14], iconAnchor: [7, 7] }) }).addTo(group);
      if (m.link) {
        marker.on("click", () => { window.open(m.link, "_blank", "noopener"); });
        const el = marker.getElement();
        if (el) { el.style.pointerEvents = "auto"; el.style.cursor = "pointer"; }
      }
    });
  }, [customMarkers]);

  // Refs for boundary polygons + layer control
  const boundariesLayerRef = useRef(null);
  const layerControlRef = useRef(null);
  const namedLayersRef = useRef({});

  // Add / refresh dynamic Yenişehir project markers & boundary polygons whenever the list changes
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    // Clean up previous project layers
    if (projectsLayerRef.current) { projectsLayerRef.current.remove(); projectsLayerRef.current = null; }
    if (boundariesLayerRef.current) { boundariesLayerRef.current.remove(); boundariesLayerRef.current = null; }

    if (!Array.isArray(projectMarkers) || projectMarkers.length === 0) return;

    // Group projects by layer_group so the layer switcher can toggle them
    const groups = {};
    projectMarkers.forEach((p) => {
      const g = p.layer_group || "Devam Eden Projeler";
      (groups[g] = groups[g] || []).push(p);
    });

    // One layerGroup per group name so control.layers can hide/show each independently
    const layerGroups = {};
    const boundaryGroups = {};

    Object.entries(groups).forEach(([gname, list]) => {
      const markerGroup = L.layerGroup();
      const boundaryGroup = L.layerGroup();
      layerGroups[gname] = markerGroup;
      boundaryGroups[gname] = boundaryGroup;

      list.forEach((p) => {
        const lat = parseFloat(p.lat); const lng = parseFloat(p.lng);
        if (!Number.isFinite(lat) || !Number.isFinite(lng) || !p.slug) return;

        // Marker with reference-style rich popup
        const html = `<div class="yn-project-marker"><span class="dot"></span><span class="label">▲ ${p.name}</span></div>`;
        const marker = L.marker([lat, lng], { icon: L.divIcon({ html, className: "yn-poi-icon", iconSize: [14, 14], iconAnchor: [7, 7] }) });
        const heroSrc = p.hero_image ? (p.hero_image.startsWith("http") ? p.hero_image : `${process.env.REACT_APP_BACKEND_URL}${p.hero_image}`) : "";
        const safeHero = escapeHtml(heroSrc).replace(/'/g, "%27");
        const safeSummary = p.summary ? escapeHtml(String(p.summary).slice(0, 140)) + (String(p.summary).length > 140 ? "…" : "") : "";
        const popupHtml = `
          <div class="yn-map-popup">
            ${heroSrc ? `<div class="yn-map-popup-image" style="background-image:url('${safeHero}');"></div>` : ""}
            <div class="yn-map-popup-body">
              <div class="yn-map-popup-cat">${escapeHtml(p.category || "")}</div>
              <div class="yn-map-popup-title">${escapeHtml(p.name)}</div>
              ${safeSummary ? `<div class="yn-map-popup-summary">${safeSummary}</div>` : ""}
              <a href="/yenisehir-projeleri/${encodeURIComponent(p.slug)}" target="_blank" rel="noopener" class="yn-map-popup-btn">
                <span>i</span> Bilgilendirme
              </a>
            </div>
          </div>`;
        marker.bindPopup(popupHtml, { className: "yn-map-popup-wrapper", maxWidth: 320, minWidth: 260, closeButton: true, autoPan: true });
        marker.addTo(markerGroup);
        const el = marker.getElement();
        if (el) { el.style.pointerEvents = "auto"; el.style.cursor = "pointer"; el.setAttribute("title", p.name); }

        // Boundary GeoJSON polygon (if configured)
        if (p.boundary_geojson) {
          try {
            const gj = typeof p.boundary_geojson === "string" ? JSON.parse(p.boundary_geojson) : p.boundary_geojson;
            const bl = L.geoJSON(gj, {
              style: {
                color: p.boundary_color || "#22346B",
                weight: 2.4,
                opacity: 0.95,
                fillColor: p.boundary_fill || "#22346B",
                fillOpacity: Number.isFinite(p.boundary_fill_opacity) ? p.boundary_fill_opacity : 0.18,
                dashArray: "4 3",
              },
              onEachFeature: (feature, lyr) => {
                lyr.bindPopup(popupHtml, { className: "yn-map-popup-wrapper", maxWidth: 320, minWidth: 260 });
              },
            });
            bl.addTo(boundaryGroup);
          } catch (e) {
            console.warn("[YenisehirPage] Invalid boundary GeoJSON for project, skipping:", p?.slug || p?.name, e);
          }
        }
      });

      markerGroup.addTo(map);
      boundaryGroup.addTo(map);
    });

    // Track all layers that we actually added to the map so cleanup on
    // next projectMarkers change (or unmount) removes them properly.
    // (The reviewer noted that plain L.layerGroup(Object.values(...)) never
    // gets added to the map, so its `.remove()` is a no-op.)
    const addedCombinedLayers = [];
    const overlays = {};
    Object.entries(layerGroups).forEach(([gname, lg]) => {
      const b = boundaryGroups[gname];
      // A combined group so toggling the checkbox hides both markers + boundaries
      const combined = L.layerGroup([lg, b]).addTo(map);
      addedCombinedLayers.push(combined);
      overlays[`<span style="color:#22346B;font-weight:600">■</span> ${gname}`] = combined;
    });
    projectsLayerRef.current = { remove: () => addedCombinedLayers.forEach((l) => { try { map.removeLayer(l); } catch (err) { console.debug("[YenisehirPage] Layer already removed", err); } }) };
    boundariesLayerRef.current = null; // combined layers already own the boundary groups

    // Build / rebuild the Layer Control (top-right dropdown)
    if (layerControlRef.current) { layerControlRef.current.remove(); layerControlRef.current = null; }
    // Also expose imar-plani (external parcels) layer if it exists
    if (namedLayersRef.current["İmar Planı (Parseller)"]) {
      overlays['<span style="color:#c9a227;font-weight:600">■</span> İmar Planı (Parseller)'] = namedLayersRef.current["İmar Planı (Parseller)"];
    }
    layerControlRef.current = L.control.layers(null, overlays, { position: "topright", collapsed: false }).addTo(map);
  }, [projectMarkers]);

  // Load imar-plani GeoJSON
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !imarUrl) return;

    setLoadingParcels(true);
    const cacheKey = "yn-imar-geojson-v1";
    const cachedAt = Number(sessionStorage.getItem(cacheKey + ":ts") || 0);
    const cached = Date.now() - cachedAt < 3600 * 1000 ? sessionStorage.getItem(cacheKey) : null;
    const promise = cached
      ? Promise.resolve(JSON.parse(cached))
      : fetch(imarUrl).then((r) => r.json()).then((d) => {
          try {
            sessionStorage.setItem(cacheKey, JSON.stringify(d));
            sessionStorage.setItem(cacheKey + ":ts", String(Date.now()));
          } catch (err) {
            // sessionStorage quota / private mode — fine to skip caching
            console.debug("[YenisehirPage] sessionStorage cache skipped:", err?.message);
          }
          return d;
        });

    promise
      .then((gj) => {
        const feats = gj.features || [];
        setParcelCount(feats.length);
        const cats = Array.from(new Set(feats.map((f) => f.properties?.kategori).filter(Boolean))).sort();
        setCategoryList(cats);

        // Remove previous layer
        if (geoLayerRef.current) map.removeLayer(geoLayerRef.current);

        const layer = L.geoJSON(gj, {
          filter: (f) => {
            // Only render polygons (skip broken Point/LineString rows that create default marker icons)
            const t = f?.geometry?.type;
            return t === "Polygon" || t === "MultiPolygon";
          },
          style: (f) => {
            const c = categoryColor(f.properties?.kategori);
            return {
              color: c,
              weight: 0.5,
              opacity: 0.85,
              fillColor: c,
              fillOpacity: 0.35,
            };
          },
          onEachFeature: (feature, lyr) => {
            const props = feature.properties || {};
            // categoryLabel is a whitelisted lookup so already safe; escape all
            // remaining fields since they originate from an external GeoJSON API.
            const tooltip = `<div style="font-family:'Inter',sans-serif;font-size:11px;background:rgba(11,22,49,0.95);color:#fff;padding:6px 10px;border:1px solid rgba(201,162,39,0.4);white-space:nowrap;">
              <div style="letter-spacing:0.22em;text-transform:uppercase;color:#C9A227;font-size:9px;">${escapeHtml(categoryLabel(props.kategori) || "Parsel")}</div>
              <div style="margin-top:2px;">Ada/Parsel · ${escapeHtml(props.ada_parsel || props.name || "—")}</div>
              ${props.fonksiyon ? `<div style="opacity:0.75;">${escapeHtml(props.fonksiyon)}</div>` : ""}
            </div>`;
            lyr.bindTooltip(tooltip, { sticky: true, direction: "top", offset: [0, -8], opacity: 1, className: "yn-leaflet-tooltip" });

            lyr.on("mouseover", () => lyr.setStyle({ weight: 2.4, fillOpacity: 0.7 }));
            lyr.on("mouseout", () => lyr.setStyle({ weight: 0.5, fillOpacity: 0.35 }));
            lyr.on("click", () => setSelected({ ...props, _geom: feature.geometry }));
          },
        }).addTo(map);
        geoLayerRef.current = layer;
        namedLayersRef.current["İmar Planı (Parseller)"] = layer;
        // If the layer control is already built, add this overlay to it
        if (layerControlRef.current) {
          layerControlRef.current.addOverlay(layer, '<span style="color:#c9a227;font-weight:600">■</span> İmar Planı (Parseller)');
        }
        // Fit to bounds gently only on first load
        try {
          map.fitBounds(layer.getBounds(), { padding: [30, 30], maxZoom: 13 });
        } catch (err) {
          console.debug("[YenisehirPage] fitBounds skipped — layer has no bounds:", err?.message);
        }
      })
      .catch((err) => { console.warn("Imar GeoJSON fetch failed:", err); })
      .finally(() => setLoadingParcels(false));
  }, [imarUrl]);

  // Apply category filter
  useEffect(() => {
    const layer = geoLayerRef.current;
    if (!layer) return;
    layer.eachLayer((l) => {
      const cat = l.feature?.properties?.kategori;
      const visible = category === "Tümü" || cat === category;
      if (visible) {
        l.setStyle({ opacity: 0.85, fillOpacity: 0.35 });
        if (l._path) l._path.style.pointerEvents = "";
      } else {
        l.setStyle({ opacity: 0, fillOpacity: 0 });
        if (l._path) l._path.style.pointerEvents = "none";
      }
    });
  }, [category]);

  const eipatLink = selected ? `${eipatBase || "https://e-ipat.com"}/?plan=yenisehir&ada=${encodeURIComponent(selected.ada_parsel?.split("/")[0] || "")}&parsel=${encodeURIComponent(selected.ada_parsel?.split("/")[1] || "")}` : "";

  return (
    <div className="relative w-full h-full" data-testid="yn-map">
      <div ref={ref} className="w-full h-full" style={{ background: "#0a1428" }} />

      {/* Legend / Filters — top left */}
      <div className="absolute top-4 left-4 yn-glass p-3 md:p-4 max-w-[calc(100%-2rem)] md:max-w-md z-[500]" data-testid="yn-map-legend">
        <div className="flex items-center justify-between mb-3 gap-4">
          <div>
            <div className="text-[0.6rem] tracking-[0.28em] uppercase text-brand-gold">İmar Planı Katmanı</div>
            <div className="text-xs text-white/70 mt-0.5">
              {loadingParcels ? "Parseller yükleniyor…" : parcelCount > 0 ? `${parcelCount.toLocaleString("tr-TR")} parsel · e-iPAT` : "—"}
            </div>
          </div>
          <a href="https://e-ipat.com" target="_blank" rel="noreferrer" data-hover className="text-[0.6rem] tracking-[0.24em] uppercase text-brand-gold/80 hover:text-brand-gold whitespace-nowrap">e-iPAT ↗</a>
        </div>
        {categoryList.length > 0 ? (
          <div className="flex flex-wrap gap-1.5">
            <button
              onClick={() => setCategory("Tümü")}
              data-hover
              className={`text-[0.6rem] tracking-[0.22em] uppercase px-2.5 py-1 border transition-colors ${category === "Tümü" ? "bg-brand-gold text-brand-navy border-brand-gold" : "border-white/20 text-white/80 hover:border-brand-gold hover:text-brand-gold"}`}
              data-testid="yn-cat-all"
            >Tümü · {parcelCount}</button>
            {categoryList.map((c) => (
              <button
                key={c}
                onClick={() => setCategory(c)}
                data-hover
                className={`text-[0.6rem] tracking-[0.22em] uppercase px-2.5 py-1 border transition-colors ${category === c ? "bg-brand-gold text-brand-navy border-brand-gold" : "border-white/20 text-white/80 hover:border-brand-gold hover:text-brand-gold"}`}
                style={{ borderColor: category === c ? "#C9A227" : `${categoryColor(c)}55` }}
                data-testid={`yn-cat-${c}`}
              >
                <span className="inline-block w-1.5 h-1.5 rounded-full mr-1.5 align-middle" style={{ background: categoryColor(c) }} />
                {categoryLabel(c)}
              </button>
            ))}
          </div>
        ) : null}
      </div>

      {/* Parcel detail panel */}
      {selected ? (
        <div className="absolute top-4 right-4 md:right-14 yn-glass p-6 w-[92%] sm:w-[380px] z-[500] max-h-[85%] overflow-y-auto" data-testid="yn-parcel-detail">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="text-[0.6rem] tracking-[0.28em] uppercase text-brand-gold">Parsel</div>
              <div className="font-serif text-3xl text-white mt-1">{selected.ada_parsel || selected.name || "—"}</div>
            </div>
            <button onClick={() => setSelected(null)} data-hover className="text-white/50 hover:text-white text-xl leading-none" data-testid="yn-close-parcel">×</button>
          </div>

          <div className="mt-6 space-y-4 text-sm">
            {selected.kategori ? (
              <div className="flex items-center justify-between">
                <span className="text-white/50 text-[0.62rem] tracking-[0.24em] uppercase">Kategori</span>
                <span className="inline-flex items-center gap-2 text-white">
                  <span className="inline-block w-2 h-2 rounded-full" style={{ background: categoryColor(selected.kategori) }} />
                  {categoryLabel(selected.kategori)}
                </span>
              </div>
            ) : null}
            {selected.fonksiyon ? (
              <div>
                <div className="text-white/50 text-[0.62rem] tracking-[0.24em] uppercase mb-1">Fonksiyon</div>
                <div className="text-white/90">{selected.fonksiyon}</div>
              </div>
            ) : null}
            {selected.SEMBOL_DTA ? (
              <div>
                <div className="text-white/50 text-[0.62rem] tracking-[0.24em] uppercase mb-1">Yapı Şartı</div>
                <div className="text-white/90 font-mono text-xs bg-white/5 px-3 py-2">{selected.SEMBOL_DTA}</div>
              </div>
            ) : null}
            {selected.TAPUALANI ? (
              <div className="flex items-center justify-between">
                <span className="text-white/50 text-[0.62rem] tracking-[0.24em] uppercase">Tapu Alanı</span>
                <span className="text-white font-medium tabular-nums">{Number(selected.TAPUALANI).toLocaleString("tr-TR", { maximumFractionDigits: 1 })} m²</span>
              </div>
            ) : null}
          </div>

          <div className="mt-8 flex flex-col gap-3">
            <a
              href={eipatLink}
              target="_blank"
              rel="noreferrer"
              data-hover
              data-testid="yn-eipat-link"
              className="inline-flex items-center justify-center gap-2 bg-brand-gold text-brand-navy px-4 py-3 text-[0.66rem] tracking-[0.28em] uppercase font-medium hover:bg-white transition-colors"
            >
              e-iPAT&apos;ta Detaylı Rapor →
            </a>
            <a
              href="#c-09"
              onClick={(e) => {
                if (onParcelInquiry) onParcelInquiry({
                  ada_parsel: selected.ada_parsel,
                  kategori: selected.kategori,
                  fonksiyon: selected.fonksiyon,
                });
                setSelected(null);
              }}
              data-hover
              data-testid="yn-inquire-parcel"
              className="inline-flex items-center justify-center gap-2 border border-white/25 text-white/90 px-4 py-3 text-[0.66rem] tracking-[0.28em] uppercase hover:border-brand-gold hover:text-brand-gold transition-colors"
            >
              Bu Parseli İncele — Danış
            </a>
          </div>
          <div className="mt-4 text-[10px] text-white/40 leading-relaxed">
            Veri kaynağı: e-iPAT · <a href="https://parselsorgu.tkgm.gov.tr/" target="_blank" rel="noreferrer" className="underline">TKGM</a>. Yalnızca bilgilendirme amaçlıdır.
          </div>
        </div>
      ) : null}

      {loadingParcels ? (
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 yn-glass px-4 py-2 text-xs text-white/80 tracking-[0.2em] uppercase z-[500] flex items-center gap-2">
          <span className="inline-block w-1.5 h-1.5 rounded-full bg-brand-gold animate-pulse" />
          {parcelCount || "4303"} parsel yükleniyor…
        </div>
      ) : null}
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* Sections                                                             */
/* ------------------------------------------------------------------ */
const chapters = [
  { n: "01", label: "Gelecek" },
  { n: "02", label: "Neden Burası" },
  { n: "03", label: "Devlet Vizyonu" },
  { n: "04", label: "Altyapı" },
  { n: "05", label: "Zaman Çizelgesi" },
  { n: "06", label: "Yatırım Potansiyeli" },
  { n: "07", label: "Projeler" },
  { n: "08", label: "Yatırımcılar" },
  { n: "09", label: "İletişim" },
];

/* ------------------------------------------------------------------ */
/* Page                                                                 */
/* ------------------------------------------------------------------ */
const YenisehirPage = () => {
  const s = useSiteSettings();
  const isMobile = useIsMobile();
  const [loading, setLoading] = useState(true);
  const [selectedParcel, setSelectedParcel] = useState(null);
  const [projects, setProjects] = useState([]);
  const rootRef = useRef(null);
  useReveal();

  // Fetch dynamic Yenişehir projects for the map markers
  useEffect(() => {
    api.get("/public/yenisehir-projects")
      .then((r) => setProjects(r.data || []))
      .catch(() => setProjects([]));
  }, []);

  // Loading — hide after ~2.4s to let logo stroke complete
  useEffect(() => {
    const t = setTimeout(() => setLoading(false), 2400);
    return () => clearTimeout(t);
  }, []);

  // Native browser scroll — Lenis smooth scroll disabled per user feedback (2026-02).
  // GSAP ScrollTrigger still works natively; only the extra "buttery" easing is gone.
  useLayoutEffect(() => {
    if (typeof window === "undefined") return;
    // Ensure ScrollTrigger keeps in sync with native scroll (in case a previous
    // Lenis-driven tick loop was still bound from an older render).
    const onScroll = () => ScrollTrigger.update();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // GSAP hero + chapter timeline
  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      // Hero parallax
      gsap.to(".yn-hero-video", { yPercent: 20, ease: "none", scrollTrigger: { trigger: ".yn-hero", start: "top top", end: "bottom top", scrub: true } });
      gsap.to(".yn-hero-content", { yPercent: -30, opacity: 0, ease: "none", scrollTrigger: { trigger: ".yn-hero", start: "top top", end: "bottom top", scrub: true } });

      // Chapter pinning intro
      gsap.utils.toArray(".yn-chapter-title").forEach((el) => {
        gsap.fromTo(el, { opacity: 0, y: 60 }, {
          opacity: 1, y: 0, duration: 1.2, ease: "power3.out",
          scrollTrigger: { trigger: el, start: "top 78%" },
        });
      });

      // Horizontal projects gallery (if wide enough)
      const proj = document.querySelector(".yn-projects-track");
      if (proj) {
        const items = proj.querySelectorAll(".yn-project-card");
        if (window.innerWidth >= 900 && items.length > 2) {
          gsap.to(proj, {
            x: () => -(proj.scrollWidth - window.innerWidth + 80),
            ease: "none",
            scrollTrigger: {
              trigger: ".yn-projects",
              start: "top top",
              end: () => "+=" + (proj.scrollWidth - window.innerWidth),
              pin: true,
              scrub: 0.6,
            },
          });
        }
      }
    }, rootRef);
    return () => ctx.revert();
  }, []);

  const heroVideoRaw = s.yenisehir_hero_video_url || PEXELS_FALLBACK_VIDEO;
  // Detect YouTube — if so, build a muted-autoplay-loop embed (chrome-less for cinematic hero)
  const heroYoutubeId = (() => {
    const m = heroVideoRaw.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/shorts\/|youtube\.com\/embed\/)([A-Za-z0-9_-]{11})/);
    return m ? m[1] : "";
  })();
  const heroIsYoutube = !!heroYoutubeId;
  const heroYoutubeEmbed = heroIsYoutube
    ? `https://www.youtube.com/embed/${heroYoutubeId}?autoplay=1&mute=1&loop=1&playlist=${heroYoutubeId}&controls=0&showinfo=0&rel=0&modestbranding=1&playsinline=1&disablekb=1&iv_load_policy=3&fs=0`
    : "";
  const phone = s.real_estate_phone || s.contact_phone || "";
  const wa = (s.real_estate_whatsapp || s.whatsapp_number || "").replace(/\D/g, "");
  const bitrixForms = s.bitrix_forms || [];
  const waLink = wa ? `https://wa.me/${wa}?text=${encodeURIComponent("Yenişehir yatırım fırsatları hakkında bilgi almak istiyorum.")}` : "#";

  const ceoName = s.yenisehir_ceo_name || "Fırat Construction Yönetim Kurulu";
  const ceoTitle = s.yenisehir_ceo_title || "Kurucu & CEO";
  const ceoBio = s.yenisehir_ceo_bio || "30 yılı aşkın deneyim ile İstanbul'un stratejik lokasyonlarında değer üretiyoruz. Yenişehir vizyonumuz, Türkiye'nin gelecek dönem yatırım koridorunu şekillendirme misyonuyla başladı.";
  const ceoPortrait = resolveImage(s.yenisehir_ceo_portrait) || "https://images.unsplash.com/photo-1560250097-0b93528c311a?auto=format&fit=crop&w=800&q=80";
  const ceoPortraitVideo = resolveImage(s.yenisehir_ceo_portrait_video) || "";
  // Normalize CEO video → YouTube embed URL if user pasted watch/short link
  const ceoVideoEmbed = (() => {
    const url = (s.yenisehir_ceo_video_url || "").trim();
    if (!url) return "";
    const yt = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/shorts\/|youtube\.com\/embed\/)([A-Za-z0-9_-]{11})/);
    if (yt) return `https://www.youtube.com/embed/${yt[1]}?rel=0&modestbranding=1`;
    return url; // already an embed url or mp4
  })();
  const testimonials = (s.yenisehir_testimonials && s.yenisehir_testimonials.length > 0) ? s.yenisehir_testimonials : [
    { name: "Mehmet Y.", title: "Yatırımcı, Ankara", quote: "Yenişehir'de aldığım arsanın değeri 3 yılda 4 kat arttı. Fırat ekibinin öngörüsü inanılmaz.", portrait: "" },
    { name: "Elif D.", title: "Portföy Yöneticisi", quote: "Şeffaflık ve profesyonellik. Sözleşmeden tapu devrine kadar her adım kusursuz.", portrait: "" },
    { name: "Ahmet K.", title: "Girişimci", quote: "İstanbul'un yeni merkezine güvenli giriş noktası. Fırat'ın uzman kadrosu güvenilir bir yol arkadaşı.", portrait: "" },
  ];

  // JSON-LD Rich Schema
  const jsonLd = useMemo(() => ({
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Place",
        "name": "İstanbul Yenişehir - Arnavutköy Yatırım Bölgesi",
        "description": "İstanbul'un yeni değerlenme merkezi. 3. Havalimanı, Kanal İstanbul ve Kuzey Marmara Otoyolu aksında premium arsa yatırım fırsatları.",
        "address": { "@type": "PostalAddress", "addressLocality": "Arnavutköy", "addressRegion": "İstanbul", "addressCountry": "TR" },
        "geo": { "@type": "GeoCoordinates", "latitude": 41.20, "longitude": 28.68 },
      },
      {
        "@type": "RealEstateAgent",
        "name": "Fırat Construction Yapı A.Ş.",
        "url": "https://firatconstruction.com/yenisehir",
        "areaServed": "İstanbul Yenişehir",
        "logo": "https://firatconstruction.com/logo.png",
      },
      {
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Ana Sayfa", "item": "https://firatconstruction.com/" },
          { "@type": "ListItem", "position": 2, "name": "İstanbul Yenişehir", "item": "https://firatconstruction.com/yenisehir" },
        ],
      },
      {
        "@type": "FAQPage",
        "mainEntity": [
          { "@type": "Question", "name": "Yenişehir nerede?", "acceptedAnswer": { "@type": "Answer", "text": "İstanbul Arnavutköy ilçesinde, İstanbul Havalimanı'nın güneyinde konumlanan yeni imarlı yerleşim ve yatırım koridorudur. Kanal İstanbul güzergâhına ve Kuzey Marmara Otoyolu'na dakikalar mesafededir." } },
          { "@type": "Question", "name": "Yenişehir'de arsa yatırımı güvenli mi?", "acceptedAnswer": { "@type": "Answer", "text": "Portföyümüzdeki tüm arsalar tapulu, ipoteksiz ve müstakildir. Sözleşme öncesi güncel tapu kaydı, takyidat raporu ve imar durumu şeffaf sunulur." } },
          { "@type": "Question", "name": "Yenişehir'de değerlenme potansiyeli nedir?", "acceptedAnswer": { "@type": "Answer", "text": "3. Havalimanı ve Kanal İstanbul etkisiyle bölgede son 5 yılda arsa fiyatları katlanarak artmıştır. Uzun vadeli değerlenme potansiyeli TÜİK ve resmi imar planlarıyla desteklenmektedir." } },
        ],
      },
    ],
  }), []);

  return (
    <div className="yn-root yn-light bg-brand-light text-brand-navy min-h-screen pb-[70px] md:pb-0" ref={rootRef} data-testid="yenisehir-page">
      <Helmet>
        <html lang="tr" />
        <title>İstanbul Yenişehir · Arnavutköy Yatırım Rehberi · Fırat Construction</title>
        <meta name="description" content="İstanbul Yenişehir - Arnavutköy'ün resmi yatırım rehberi. 3. Havalimanı, Kanal İstanbul ve Kuzey Marmara Otoyolu aksında premium arsa fırsatları. 30 yıllık deneyimle Fırat Construction." />
        <meta name="keywords" content="İstanbul Yenişehir, Arnavutköy Yenişehir, İstanbul Yenişehir Projesi, Yenişehir İstanbul, Arnavutköy yatırım, İstanbul arsa yatırımı, İstanbul yatırım fırsatları, Yenişehir arsa, İstanbul Yenişehir konut, İstanbul Yenişehir yatırım rehberi" />
        <link rel="canonical" href="https://firatconstruction.com/yenisehir" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="İstanbul Yenişehir · Türkiye'nin Yeni Yatırım Koridoru" />
        <meta property="og:description" content="3. Havalimanı, Kanal İstanbul ve Kuzey Marmara aksında Arnavutköy Yenişehir'de premium arsa yatırım fırsatları." />
        <meta property="og:image" content={HERO_STILL} />
        <meta property="og:url" content="https://firatconstruction.com/yenisehir" />
        <meta property="og:locale" content="tr_TR" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="İstanbul Yenişehir · Yatırım Rehberi" />
        <meta name="twitter:description" content="İstanbul'un yeni yatırım koridoru. Fırat Construction ile keşfedin." />
        <meta name="twitter:image" content={HERO_STILL} />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      {!isMobile ? <PremiumCursor /> : null}
      <ScrollProgress />

      {/* Minimal top bar */}
      <header className="fixed top-0 left-0 right-0 z-50 yn-glass">
        <div className="mx-auto max-w-[1600px] px-6 md:px-10 h-16 flex items-center justify-between">
          <a href="/" data-hover className="flex items-center" aria-label="Fırat Construction anasayfa">
            <BrandLogo place="header" max={44} />
          </a>
          <nav className="hidden md:flex items-center gap-8 text-[0.66rem] tracking-[0.32em] uppercase text-white/70">
            {chapters.slice(0, 6).map((c) => (
              <a key={c.n} href={`#c-${c.n}`} data-hover className="hover:text-brand-gold transition-colors">{c.label}</a>
            ))}
          </nav>
          <div className="flex items-center gap-3">
            {wa ? (
              <a href={waLink} target="_blank" rel="noreferrer" data-hover className="hidden sm:inline-flex items-center gap-2 border border-brand-gold/50 text-brand-gold hover:bg-brand-gold hover:text-brand-navy transition-colors text-[0.7rem] tracking-[0.28em] uppercase px-4 py-2">
                <MessageCircle size={13} /> WhatsApp
              </a>
            ) : null}
            <select data-hover className="bg-transparent text-white/70 text-xs border-none focus:outline-none" defaultValue="tr">
              <option value="tr">TR</option><option value="en">EN</option>
            </select>
          </div>
        </div>
      </header>

      {/* ==================================================== HERO */}
      <section className="yn-hero relative h-screen w-full overflow-hidden" id="c-01" data-testid="yn-hero">
        <div className="yn-hero-video absolute inset-0 w-full h-[120%]">
          {heroIsYoutube ? (
            <div className="absolute inset-0 w-full h-full pointer-events-none">
              {/* YouTube 16:9 cover fill via 177.77vh × 100vw (or vice versa) */}
              <iframe
                src={heroYoutubeEmbed}
                title="Yenişehir hero"
                className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
                style={{ width: "max(100vw, 177.78vh)", height: "max(56.25vw, 100vh)", border: 0 }}
                allow="autoplay; encrypted-media; picture-in-picture"
                allowFullScreen={false}
                loading="eager"
                data-testid="yn-hero-youtube"
              />
            </div>
          ) : (
            <video autoPlay muted loop playsInline poster={HERO_STILL} className="w-full h-full object-cover">
              <source src={heroVideoRaw} type="video/mp4" />
            </video>
          )}
          <div className="absolute inset-0 bg-gradient-to-b from-[#050B1E]/50 via-[#050B1E]/60 to-[#050B1E]" />
        </div>
        <div className="yn-grain" />

        <div className="yn-hero-content relative z-10 h-full flex flex-col justify-end pb-24 md:pb-32 mx-auto max-w-[1600px] px-6 md:px-16">
          <div className="text-[0.7rem] tracking-[0.5em] uppercase text-brand-gold yn-reveal">İstanbul · Arnavutköy · Yenişehir</div>
          <h1 className="mt-6 font-serif leading-[0.95] tracking-tight text-white yn-mask-reveal" style={{ fontSize: "clamp(2.5rem, 8vw, 8.5rem)" }}>
            <span>İstanbul&apos;un<br />Geleceği.</span>
          </h1>
          <p className="mt-6 max-w-2xl text-base md:text-xl text-white/80 leading-relaxed yn-reveal">
            3. Havalimanı, Kanal İstanbul ve Kuzey Marmara aksında yeni bir metropol yükseliyor. Tapulu, imarlı arsalarla İstanbul&apos;un geleceğine bugünden yatırım yapın.
          </p>
          <div className="mt-6 flex flex-wrap gap-2 yn-reveal">
            {["8 dk · 3. Havalimanı", "Kanal İstanbul aksı", "Tapulu & imarlı", "5 yılda 4× değer"].map((c) => (
              <span key={c} className="inline-flex items-center gap-2 text-[0.68rem] md:text-xs tracking-wide text-white/90 bg-white/10 border border-white/15 backdrop-blur px-3 py-1.5 rounded-full">
                <span className="w-1.5 h-1.5 rounded-full bg-brand-gold" /> {c}
              </span>
            ))}
          </div>
          <div className="mt-8 md:mt-10 flex flex-col sm:flex-row flex-wrap gap-3 sm:gap-4">
            <a href="#c-09" data-hover className="inline-flex items-center justify-center gap-3 w-full sm:w-auto bg-brand-gold text-brand-navy px-8 py-4 text-[0.72rem] tracking-[0.3em] uppercase font-medium hover:bg-white transition-colors">
              Yatırım Danışmanı Bağla <Sparkles size={14} />
            </a>
            <a href="/yenisehir-projeleri" data-hover className="inline-flex items-center justify-center gap-3 w-full sm:w-auto border border-white/25 hover:border-brand-gold hover:text-brand-gold text-white/90 px-8 py-4 text-[0.72rem] tracking-[0.3em] uppercase transition-colors">
              Mega Projeler Rehberi
            </a>
            <a href="#neden" data-hover className="inline-flex items-center justify-center gap-3 w-full sm:w-auto border border-white/25 hover:border-brand-gold hover:text-brand-gold text-white/90 px-8 py-4 text-[0.72rem] tracking-[0.3em] uppercase transition-colors">
              Keşfetmeye Başla <ChevronDown size={14} />
            </a>
          </div>
        </div>
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 text-white/40 text-[0.62rem] tracking-[0.4em] uppercase flex flex-col items-center gap-3">
          <span>Kaydır</span>
          <ChevronDown size={16} className="animate-bounce" />
        </div>
      </section>

      {/* ==================================================== NEDEN YENİŞEHİR — value props */}
      <section id="neden" className="relative py-14 md:py-24 bg-brand-navy yn-keep-dark" data-testid="yn-why">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10">
          <div className="text-[0.6rem] tracking-[0.4em] uppercase text-brand-gold">Neden Yenişehir?</div>
          <h2 className="mt-4 font-serif leading-[1.05] tracking-tight text-white" style={{ fontSize: "clamp(1.7rem, 5vw, 3.25rem)" }}>
            İstanbul&apos;un değer kazanan <em className="text-brand-gold not-italic">yeni merkezi.</em>
          </h2>
          <p className="mt-4 max-w-2xl text-white/70 leading-relaxed text-sm md:text-base">
            Devlet destekli mega altyapı yatırımları Yenişehir&apos;i İstanbul&apos;un bir sonraki cazibe merkezine dönüştürüyor. Bölgeyi bugün yatırım için eşsiz kılan nedenler:
          </p>
          <div className="mt-10 md:mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
            {[
              { Icon: Plane, t: "8 Dakika Havalimanı", d: "İstanbul Havalimanı'na dakikalar mesafede — küresel bağlantı ayağınızın altında." },
              { Icon: Waves, t: "Kanal İstanbul Aksı", d: "Dünyanın en büyük altyapı projesinin doğrudan etki ve değerlenme alanında." },
              { Icon: TrainFront, t: "Kuzey Marmara Otoyolu", d: "Şehir merkezleri, limanlar ve sanayiye kesintisiz ulaşım koridoru." },
              { Icon: TrendingUp, t: "5 Yılda 4× Değer", d: "Resmî imar planları ve artan talep ile güçlü, kanıtlı değerlenme potansiyeli." },
            ].map(({ Icon, t, d }) => (
              <div key={t} className="border border-white/12 bg-white/[0.03] p-6 md:p-7 hover:border-brand-gold/50 transition-colors" data-testid={`yn-why-card`}>
                <div className="w-12 h-12 flex items-center justify-center border border-brand-gold/40 text-brand-gold">
                  <Icon size={22} strokeWidth={1.4} />
                </div>
                <h3 className="mt-5 font-serif text-xl text-white leading-snug">{t}</h3>
                <p className="mt-2.5 text-sm text-white/65 leading-relaxed">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ==================================================== CHAPTER 02 — MEGA PROJECTS */}
      <section id="c-02" className="relative py-14 md:py-20 bg-white">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <div>
              <div className="text-[0.6rem] tracking-[0.4em] uppercase text-brand-primary">Bölüm 02 · Bölge Portföyü</div>
              <h2 className="mt-4 font-serif leading-[1.02] tracking-tight text-brand-navy" style={{ fontSize: "clamp(1.6rem, 4vw, 3rem)" }}>
                Yenişehir&apos;deki <em className="text-brand-primary not-italic">mega projeler.</em>
              </h2>
              <p className="mt-3 max-w-2xl text-sm text-brand-dark/75 leading-relaxed">
                Yenişehir&apos;in kaderini şekillendiren <strong className="text-brand-navy">{projects.length} kamu ve özel sektör yatırımı</strong>.
              </p>
            </div>
            <Link to="/yenisehir-projeleri" className="inline-flex items-center gap-2 self-start md:self-auto border border-brand-navy text-brand-navy hover:bg-brand-navy hover:text-white px-5 py-2.5 text-[0.62rem] tracking-[0.28em] uppercase transition-colors whitespace-nowrap" data-testid="c02-see-all">
              Tümünü Gör <ArrowRight size={12} />
            </Link>
          </div>

          <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
            {projects.slice(0, 4).map((p, i) => (
              <Link
                to={`/yenisehir-projeleri/${p.slug}`}
                key={p.id || p.slug}
                className="group bg-white border border-brand-navy/10 hover:border-brand-primary hover:shadow-[0_14px_40px_-24px_rgba(26,42,90,0.35)] transition-all duration-400 overflow-hidden"
                data-testid={`c02-project-${p.slug}`}
              >
                <div className="relative aspect-[4/3] overflow-hidden bg-gradient-to-br from-brand-primary/10 via-white to-brand-primary/5">
                  {p.hero_image ? (
                    <img
                      src={p.hero_image.startsWith("http") ? p.hero_image : `${process.env.REACT_APP_BACKEND_URL}${p.hero_image}`}
                      alt={p.name}
                      className="w-full h-full object-cover group-hover:scale-[1.05] transition-transform duration-500"
                      loading="lazy"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <div className="text-center">
                        <div className="text-3xl text-brand-primary/25">▲</div>
                        <div className="mt-1 text-[0.5rem] tracking-[0.28em] uppercase text-brand-primary/45">Yenişehir</div>
                      </div>
                    </div>
                  )}
                  <div className="absolute top-2 left-2 text-[0.5rem] tracking-[0.22em] uppercase text-white bg-brand-navy/90 backdrop-blur px-1.5 py-0.5">#{String(i + 1).padStart(2, "0")}</div>
                </div>
                <div className="p-3.5">
                  <div className="text-[0.5rem] tracking-[0.28em] uppercase text-brand-primary line-clamp-1">{p.category}</div>
                  <h3 className="mt-1.5 font-serif text-sm md:text-base text-brand-navy leading-snug line-clamp-2 group-hover:text-brand-primary transition-colors">{p.name}</h3>
                  <div className="mt-2.5 flex items-center justify-between">
                    <span className="text-[0.6rem] text-brand-medium line-clamp-1 pr-1">{p.developer}</span>
                    <span className="inline-flex items-center gap-1 text-[0.55rem] tracking-[0.22em] uppercase text-brand-primary flex-shrink-0">
                      İncele <ArrowRight size={10} />
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ==================================================== CHAPTER 03 — MAP */}
      <section id="c-03" className="relative min-h-screen">
        <div className="mx-auto max-w-[1600px] px-6 md:px-10 py-24">
          <div className="text-[0.62rem] tracking-[0.4em] uppercase text-brand-gold yn-reveal">Bölüm 03 · Devlet Vizyonu</div>
          <h2 className="yn-chapter-title mt-8 font-serif leading-[1] tracking-tight max-w-4xl" style={{ fontSize: "clamp(2rem, 5.5vw, 5rem)" }}>
            İstanbul&apos;un <em className="text-brand-gold not-italic">yeni omurgası</em>.
          </h2>
          <p className="mt-8 max-w-2xl text-white/70 leading-relaxed yn-reveal">
            Cumhurbaşkanlığı Yatırım Ofisi, T.C. Ulaştırma Bakanlığı ve Çevre Bakanlığı ortak planlamasıyla; havalimanı, kanal, otoyollar ve şehir dokusu Yenişehir merkezli entegre şekilde tasarlandı.
          </p>
        </div>
        <div className="relative w-full h-[55vh] md:h-[80vh] bg-white border-y border-white/5">
          <InteractiveMap
            imarUrl={s.yenisehir_imar_geojson_url || ""}
            eipatBase={s.yenisehir_eipat_base_url || "https://e-ipat.com"}
            customMarkers={s.yenisehir_map_markers || []}
            projectMarkers={projects}
            onParcelInquiry={setSelectedParcel}
          />
        </div>
      </section>

      {/* ==================================================== CHAPTER 04 — INFRASTRUCTURE STATS */}
      <section id="c-04" className="relative py-16 md:py-24 bg-gradient-to-b from-white via-brand-light to-white">
        <div className="mx-auto max-w-[1600px] px-6 md:px-10">
          <div className="text-[0.62rem] tracking-[0.4em] uppercase text-brand-gold yn-reveal">Bölüm 04 · Altyapı</div>
          <h2 className="yn-chapter-title mt-8 font-serif leading-[1] tracking-tight" style={{ fontSize: "clamp(2rem, 6vw, 5.5rem)" }}>
            Rakamlarla <em className="text-brand-gold not-italic">Yenişehir.</em>
          </h2>
          <div className="mt-24 grid grid-cols-2 md:grid-cols-4 gap-10 md:gap-4">
            {[
              { v: 200, s: " km²", l: "Yeni Şehir Alanı" },
              { v: 100, s: "M m²", l: "İmar Alanı" },
              { v: 8, s: " dk", l: "Havalimanı Mesafesi" },
              { v: 4, s: " kat", l: "5 Yıllık Değer Artışı" },
            ].map((k, i) => (
              <div key={i} className="yn-reveal border-l border-brand-gold/40 pl-6">
                <div className="font-serif text-6xl md:text-7xl text-white leading-none"><Counter to={k.v} suffix={k.s} /></div>
                <div className="mt-4 text-[0.68rem] tracking-[0.28em] uppercase text-white/50">{k.l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ==================================================== CHAPTER 05 — TIMELINE */}
      <section id="c-05" className="relative py-16 md:py-24">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10">
          <div className="text-[0.62rem] tracking-[0.4em] uppercase text-brand-gold yn-reveal">Bölüm 05 · Zaman Çizelgesi</div>
          <h2 className="yn-chapter-title mt-8 font-serif leading-[1] tracking-tight" style={{ fontSize: "clamp(2rem, 6vw, 5.5rem)" }}>
            Bir <em className="text-brand-gold not-italic">on yıl,</em> bir <em className="text-brand-gold not-italic">şehir.</em>
          </h2>
          <div className="mt-24 relative">
            <div className="absolute left-[24px] md:left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-transparent via-brand-gold/40 to-transparent" />
            {[
              { y: "2021", t: "Planlama", d: "İmar ve altyapı planları netleşti." },
              { y: "2022", t: "Altyapı", d: "Yollar, elektrik, doğal gaz temel altyapısı." },
              { y: "2023", t: "Konut", d: "TOKİ ilk konut projeleri teslim edildi." },
              { y: "2024", t: "İnşaat", d: "Özel sektör konut/ticari projeleri yükseldi." },
              { y: "2025", t: "Genişleme", d: "Yeni ulaşım hatları ve ticari merkezler." },
              { y: "2030+", t: "Metropol", d: "Kanal İstanbul ve tam entegre şehir tamamlandı." },
            ].map((m, i) => (
              <div key={i} className={`yn-reveal relative flex ${i % 2 === 0 ? "md:justify-start" : "md:justify-end"} pl-14 md:pl-0 py-10`}>
                <div className={`w-full md:w-[45%] ${i % 2 === 0 ? "md:pr-12 md:text-right" : "md:pl-12 md:text-left"}`}>
                  <div className="text-brand-gold font-serif text-4xl">{m.y}</div>
                  <div className="mt-2 text-2xl text-white">{m.t}</div>
                  <p className="mt-2 text-white/60 leading-relaxed">{m.d}</p>
                </div>
                <div className="absolute left-[18px] md:left-1/2 top-14 -translate-x-1/2 w-3 h-3 rounded-full bg-brand-gold shadow-[0_0_0_6px_rgba(201,162,39,0.15)]" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ==================================================== CHAPTER 07 — REFERENCE LANDS (max 3 cards + link to full page) */}
      <section id="c-07" className="yn-projects relative py-16 md:py-24 overflow-hidden">
        <div className="mx-auto max-w-[1600px] px-6 md:px-10 mb-10 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <div>
            <div className="text-[0.62rem] tracking-[0.4em] uppercase text-brand-primary yn-reveal">Bölüm 07 · Portföy</div>
            <h2 className="yn-chapter-title mt-6 font-serif leading-[1.02] tracking-tight text-brand-navy" style={{ fontSize: "clamp(2rem, 5.5vw, 4.75rem)" }}>
              <em className="text-brand-primary not-italic">Referans</em> yapılarımız.
            </h2>
          </div>
          <Link to="/yenisehir-projeleri" className="inline-flex items-center gap-2 self-start md:self-auto border border-brand-navy text-brand-navy hover:bg-brand-navy hover:text-white px-5 py-2.5 text-[0.62rem] tracking-[0.28em] uppercase transition-colors whitespace-nowrap" data-testid="c07-see-all">
            Tümünü Gör <ArrowRight size={12} />
          </Link>
        </div>
        <div className="mx-auto max-w-[1600px] px-6 md:px-10">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {(s.real_estate_opportunities || []).slice(0, 3).map((o, i) => {
              const img = resolveImage(o.image) || `https://images.unsplash.com/photo-1500382017468-9049fed747ef?auto=format&fit=crop&w=1400&q=80&sig=${i}`;
              return (
                <article key={i} data-hover className="yn-keep-dark yn-project-card group relative overflow-hidden bg-brand-navy" data-testid={`yn-project-${i}`}>
                  <div className="aspect-[4/5] overflow-hidden">
                    <img src={img} alt={o.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-[1200ms]" loading="lazy" />
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-[#050B1E] via-[#050B1E]/40 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-6 md:p-7">
                    <div className="text-[0.6rem] tracking-[0.28em] uppercase text-brand-gold flex items-center gap-2"><LandPlot size={12} /> {o.location}</div>
                    <h3 className="mt-3 font-serif text-2xl md:text-3xl text-white leading-tight">{o.title}</h3>
                    <div className="mt-3 flex items-center justify-between text-sm text-white/80">
                      <span>{o.size}</span>
                      <span className="text-brand-gold">{o.badge || "FIRSAT"}</span>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* ==================================================== CHAPTER 08 — CEO */}
      <section className="relative py-16 md:py-24 bg-white">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-5 yn-reveal">
            {ceoVideoEmbed ? (
              <div className="aspect-video overflow-hidden bg-black">
                <iframe
                  src={ceoVideoEmbed}
                  title={`${ceoName} — mesaj`}
                  className="w-full h-full"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  data-testid="yn-ceo-video"
                />
              </div>
            ) : (
              <div className="aspect-[4/5] overflow-hidden bg-brand-navy relative">
                {ceoPortraitVideo ? (
                  <video
                    src={ceoPortraitVideo}
                    poster={ceoPortrait}
                    autoPlay
                    muted
                    loop
                    playsInline
                    preload="metadata"
                    className="absolute inset-0 w-full h-full object-cover"
                    aria-label={`${ceoName} — portrait video`}
                    data-testid="yn-ceo-portrait-video-loop"
                  />
                ) : (
                  <img src={ceoPortrait} alt={ceoName} className="w-full h-full object-cover grayscale hover:grayscale-0 transition-all duration-1000" loading="lazy" />
                )}
              </div>
            )}
          </div>
          <div className="md:col-span-7">
            <div className="text-[0.62rem] tracking-[0.4em] uppercase text-brand-gold yn-reveal">Vizyoner</div>
            <blockquote className="yn-chapter-title mt-8 font-serif text-3xl md:text-5xl leading-[1.15] text-white">
              &ldquo;Yenişehir, bir emlak fırsatı değil; İstanbul&apos;un gelecek yüzyılının başkentidir.&rdquo;
            </blockquote>
            <div className="mt-10 flex items-center gap-4 yn-reveal">
              <div>
                <div className="text-white text-lg">{ceoName}</div>
                <div className="text-white/50 text-sm">{ceoTitle}</div>
              </div>
            </div>
            <p className="mt-8 max-w-2xl text-white/70 leading-relaxed yn-reveal">{ceoBio}</p>
          </div>
        </div>
      </section>

      {/* ==================================================== TESTIMONIALS */}
      <section id="c-08" className="relative py-16 md:py-24">
        <div className="mx-auto max-w-[1600px] px-6 md:px-10">
          <div className="text-[0.62rem] tracking-[0.4em] uppercase text-brand-gold yn-reveal">Bölüm 08 · Yatırımcılar</div>
          <h2 className="yn-chapter-title mt-8 font-serif leading-[1] tracking-tight" style={{ fontSize: "clamp(2rem, 5vw, 4.5rem)" }}>
            Kazanan hikâyeler.
          </h2>
          <div className="mt-20 grid grid-cols-1 md:grid-cols-3 gap-6">
            {testimonials.map((t, i) => (
              <div key={i} className="yn-reveal yn-glass p-8 md:p-10 relative overflow-hidden group" data-hover>
                <div className="absolute -top-8 -left-4 font-serif text-[9rem] text-brand-gold/10 leading-none select-none">&ldquo;</div>
                <p className="relative text-white/85 leading-relaxed italic">{t.quote}</p>
                <div className="mt-8 flex items-center gap-4">
                  {t.portrait ? (
                    <img src={resolveImage(t.portrait)} alt={t.name} className="w-12 h-12 rounded-full object-cover" />
                  ) : (
                    <div className="w-12 h-12 rounded-full bg-brand-gold/20 flex items-center justify-center text-brand-gold font-serif">{t.name?.[0] || "?"}</div>
                  )}
                  <div>
                    <div className="text-white text-sm">{t.name}</div>
                    <div className="text-white/50 text-xs">{t.title}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ==================================================== FAQ */}
      <section className="relative py-32 md:py-40 bg-white">
        <div className="mx-auto max-w-3xl px-6">
          <div className="text-[0.62rem] tracking-[0.4em] uppercase text-brand-gold yn-reveal text-center">Sık Sorulan Sorular</div>
          <h2 className="yn-chapter-title mt-6 font-serif text-4xl md:text-5xl text-center leading-tight">Aklınıza takılanlar.</h2>
          <div className="mt-16 space-y-3">
            {(s.real_estate_faq || []).map((f, i) => (
              <details key={i} className="group yn-glass" data-hover>
                <summary className="cursor-pointer list-none flex items-center justify-between px-6 py-5 text-white">
                  <span className="pr-4">{f.q}</span>
                  <ChevronDown className="shrink-0 group-open:rotate-180 transition-transform" size={18} />
                </summary>
                <div className="px-6 pb-6 text-white/70 leading-relaxed text-sm">{f.a}</div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* ==================================================== CONTACT — Bitrix "Danış & Bilgi Al" */}
      <section id="c-09" className="relative py-16 md:py-24">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-6">
            <div className="text-[0.62rem] tracking-[0.4em] uppercase text-brand-gold yn-reveal">Bölüm 09 · İletişim</div>
            <h2 className="yn-chapter-title mt-8 font-serif leading-[1] tracking-tight text-white" style={{ fontSize: "clamp(2rem, 6vw, 5rem)" }}>
              Yatırım hikâyenizi <em className="text-brand-gold not-italic">şimdi başlatın.</em>
            </h2>
            <p className="mt-8 text-white/70 leading-relaxed max-w-md yn-reveal">Uzmanlarımız fiyat listesi, imar planı ve tapu detaylarını size özel hazırlayarak arasın.</p>
            <div className="mt-12 space-y-6 yn-reveal">
              {phone ? (
                <a href={`tel:${phone}`} data-hover className="flex items-center gap-4 group">
                  <div className="w-14 h-14 border border-brand-gold/40 flex items-center justify-center group-hover:bg-brand-gold group-hover:text-brand-navy transition-colors"><Phone size={18} /></div>
                  <div>
                    <div className="text-[0.6rem] tracking-[0.28em] uppercase text-white/50">Telefon</div>
                    <div className="text-white text-lg">{phone}</div>
                  </div>
                </a>
              ) : null}
              {wa ? (
                <a href={waLink} target="_blank" rel="noreferrer" data-hover className="flex items-center gap-4 group">
                  <div className="w-14 h-14 border border-brand-gold/40 flex items-center justify-center group-hover:bg-brand-gold group-hover:text-brand-navy transition-colors"><MessageCircle size={18} /></div>
                  <div>
                    <div className="text-[0.6rem] tracking-[0.28em] uppercase text-white/50">WhatsApp</div>
                    <div className="text-white text-lg">Anında Sohbet</div>
                  </div>
                </a>
              ) : null}
            </div>
          </div>
          <div className="lg:col-span-6">
            <div className="bg-white text-brand-dark p-8 md:p-12 yn-reveal">
              <div className="text-[0.62rem] tracking-[0.3em] uppercase text-brand-medium">Ücretsiz Yatırım Dosyası</div>
              <div className="font-serif text-3xl text-brand-navy mt-3">Danış &amp; Bilgi Al</div>

              {selectedParcel ? (
                <div className="mt-6 p-4 border-l-2 border-brand-gold bg-brand-gold/5 flex items-start justify-between gap-3" data-testid="yn-selected-parcel-chip">
                  <div>
                    <div className="text-[0.62rem] tracking-[0.28em] uppercase text-brand-gold">Seçili Parsel · Formunuza otomatik eklenecek</div>
                    <div className="mt-2 font-serif text-lg text-brand-navy">Ada/Parsel {selectedParcel.ada_parsel}</div>
                    <div className="text-xs text-brand-medium mt-1">
                      {selectedParcel.fonksiyon || ""}{selectedParcel.kategori ? ` · ${selectedParcel.kategori}` : ""}
                    </div>
                    <div className="text-[10px] text-brand-medium mt-2">
                      Formda &quot;Notlar&quot; veya benzeri alana bu bilgiyi kısaca yazabilirsiniz — CRM&apos;imizde de otomatik etiketlenir.
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setSelectedParcel(null)}
                    className="text-brand-medium hover:text-red-500 text-lg leading-none"
                    aria-label="Parsel seçimini kaldır"
                    data-testid="yn-clear-parcel"
                  >×</button>
                </div>
              ) : null}

              <div className="mt-6">
                <BitrixFormEmbed
                  key={selectedParcel?.ada_parsel || "generic"}
                  forms={bitrixForms}
                  formKey="buy"
                  extraQuery={selectedParcel ? `utm_content=${encodeURIComponent("parsel_" + selectedParcel.ada_parsel)}&utm_campaign=yenisehir-parsel-inquiry` : "utm_campaign=yenisehir-general"}
                  testId="yn-bitrix"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Site Footer */}
      <div className="yn-keep-dark">
        <Footer settings={s} />
      </div>

      {/* Sticky CTAs — desktop floating */}
      <div className="hidden md:flex yn-keep-dark fixed bottom-6 right-6 z-40 flex-col gap-3">
        {wa ? (
          <a href={waLink} target="_blank" rel="noreferrer" data-hover className="group flex items-center gap-3 bg-brand-gold text-brand-navy px-5 py-3 shadow-[0_10px_40px_rgba(201,162,39,0.35)] hover:bg-white transition-colors" data-testid="yn-sticky-wa">
            <MessageCircle size={16} />
            <span className="text-[0.68rem] tracking-[0.28em] uppercase font-medium">WhatsApp</span>
          </a>
        ) : null}
        {phone ? (
          <a href={`tel:${phone}`} data-hover className="flex items-center gap-3 bg-brand-navy border border-white/20 text-white px-5 py-3 hover:border-brand-gold hover:text-brand-gold transition-colors" data-testid="yn-sticky-call">
            <Phone size={16} />
            <span className="text-[0.68rem] tracking-[0.28em] uppercase font-medium">Ara</span>
          </a>
        ) : null}
      </div>

      {/* Mobile sticky conversion bar (Google Ads) */}
      <div className="md:hidden yn-keep-dark fixed bottom-0 left-0 right-0 z-50 bg-brand-navy border-t border-white/10 px-2.5 py-2.5 flex gap-2" data-testid="yn-mobile-cta-bar">
        {wa ? (
          <a href={waLink} target="_blank" rel="noreferrer" className="flex-1 inline-flex items-center justify-center gap-1.5 bg-[#25D366] text-white py-3 rounded-lg font-medium text-[0.8rem]" data-testid="yn-mobile-wa">
            <MessageCircle size={16} /> WhatsApp
          </a>
        ) : null}
        {phone ? (
          <a href={`tel:${phone}`} className="flex-1 inline-flex items-center justify-center gap-1.5 bg-brand-gold text-brand-navy py-3 rounded-lg font-medium text-[0.8rem]" data-testid="yn-mobile-call">
            <Phone size={16} /> Hemen Ara
          </a>
        ) : null}
        <a href="#c-09" className="flex-1 inline-flex items-center justify-center gap-1.5 bg-white/10 text-white border border-white/15 py-3 rounded-lg font-medium text-[0.8rem]" data-testid="yn-mobile-form">
          <Sparkles size={15} /> Bilgi Al
        </a>
      </div>
    </div>
  );
};

export default YenisehirPage;
